import { ArrayUtils, engineComponent, Utils } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ReelsHighlightOptions, WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { GameManager } from "../../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { ITweenable, Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { StageController } from "../../Components/StageController";
import { LayoutManager } from "../../Managers/LayoutManager";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { CustomUtils } from "../../Utils/CustomUtils";
import { PaytableBoxElement } from "../../CustomComponents/PaytableBoxElement";
import { Symbol } from "../Symbol";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { TeasingMystery } from "../../CustomComponents/TeasingMystery";
import { Settings } from "../../CustomComponents/Settings";
import { LayoutSwitchElement } from "../../CustomComponents/LayoutSwitchElement";
import { IElement } from "../../Components/Elements/Interfaces/IElement";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement implements IElement, ITweenable {
    public static LayoutManager: LayoutManager;
    public static Logo: SwitchElement;
    public static Background: SwitchElement;
    public static Reels: SwitchElement;
    public static XBox: SwitchElement;
    public static paytableBoxElement: PaytableBoxElement;
    public static Messages: LayoutSwitchElement;

    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _machineStoppedTimer: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;

    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _symbolsContainer: ContainerElement;
    private _noWinContainer: ContainerElement;
    private _winAnimator: WinAnimatorExtended;

    private _teasing: SwitchElement;
    private _teasedSymbolsContainer: ContainerElement;
    private _teasingReel: SwitchElement | ContainerElement;
    private _teasingMystery: TeasingMystery;
    private _inTeasing = false;

    private _paylinesBackground: ContainerElement;
    private _background: SwitchElement;
    private _stickyContainer: ContainerElement;
    public static ReflectionX: ImageElement;
    public static ReflectionXGlow: ImageElement;

    public static isDesktopChannel: boolean;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._machineStoppedTimer = CreateFrameTimer("machineStoppedTimer");
        //this._machineStoppedTimer.on(FrameTimerEvents.Complete, this.machineStoppedDelayed.bind(this));

        BaseView.paytableBoxElement = new PaytableBoxElement();

        BaseView.isDesktopChannel = false;

        this._teasingMystery = new TeasingMystery();
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "TeasedSymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._teasedSymbolsContainer = this.childs[key];
                }

                if (key === "Teasing" && this.childs[key] instanceof SwitchElement) {
                    this._teasing = this.childs[key];
                }

                if (
                    key === "TeasingReel" &&
                    (this.childs[key] instanceof SwitchElement || this.childs[key] instanceof ContainerElement)
                ) {
                    this._teasingReel = this.childs[key];

                    if (!Utils.isNullOrUndefined(this._teasingMystery)) {
                        if (!Utils.isNullOrUndefined(this._teasingReel)) {
                            this._teasingMystery.addElementInstance({ teasingReel: this._teasingReel });
                        }
                        this._teasingMystery.eventEmitter.on(
                            this._teasingMystery.eventTeasing,
                            this.teasingMysteryTeasing.bind(this),
                        );
                        this._teasingMystery.eventEmitter.on(
                            this._teasingMystery.eventTeasingWin,
                            this.teasingMysteryTeasingWin.bind(this),
                        );
                        this._teasingMystery.eventEmitter.on(
                            this._teasingMystery.eventTeasingFail,
                            this.teasingMysteryTeasingFail.bind(this),
                        );
                        this._teasingMystery.eventEmitter.on(
                            this._teasingMystery.eventTeasingReel,
                            this.teasingMysteryAnimateOnReel.bind(this),
                        );
                        this._teasingMystery.eventEmitter.on(
                            this._teasingMystery.eventTeasingReelStopping,
                            this.teasingMysteryReelStopping.bind(this),
                        );
                    }
                }
            }

            this._noWinContainer = CustomUtils.getObjectFrom("NoWinContainer", this.childs, ContainerElement);
            this._paylinesBackground = CustomUtils.getObjectFrom("PaylinesBackground", this.childs, ContainerElement);
            this._symbolsContainer = CustomUtils.getObjectFrom("SymbolsContainer", this.childs, ContainerElement);
            this._background = CustomUtils.getObjectFrom("Background", this.childs, SwitchElement);
            this._stickyContainer = CustomUtils.getObjectFrom("StickyContainer", this.childs, ContainerElement);

            BaseView.Logo = CustomUtils.getObjectFrom("Logo", this.childs, SwitchElement);
            BaseView.ReflectionX = CustomUtils.getObjectFrom("ReflectionX", this.childs, ImageElement);
            BaseView.ReflectionXGlow = CustomUtils.getObjectFrom("ReflectionXGlow", this.childs, ImageElement);
            BaseView.Background = CustomUtils.getObjectFrom("Background", this.childs, SwitchElement);
            BaseView.Reels = CustomUtils.getObjectFrom("Reels", this.childs, SwitchElement);
            BaseView.paytableBoxElement.paytableBox = CustomUtils.getObjectFrom(
                "PaytableBox",
                this.childs,
                SwitchElement,
            );
            BaseView.XBox = CustomUtils.getObjectFrom("XBox", this.childs, SwitchElement);
            BaseView.Messages = CustomUtils.getObjectFrom("Messages", this.childs, LayoutSwitchElement);

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    protected layoutChanged() {
        if (this._wasFinished && !BaseView.paytableBoxElement.drawInProgress) {
            if (GameManager.baseResponse != null && GameManager.baseResponse.hasOwnProperty("customData")) {
                if (GameManager.baseResponse.customData.hasOwnProperty("mysterySymbol")) {
                    let mysterySymbol = GameManager.baseResponse.customData.mysterySymbol;

                    if (!Utils.isNullOrUndefined(BaseView.paytableBoxElement)) {
                        BaseView.paytableBoxElement.reset({
                            symbolsOn: false,
                            symbolsNoWinOn: true,
                            symbolsGlowOff: true,
                            multipliersGlowOff: true,
                        });
                        BaseView.paytableBoxElement.paytableBox.goToCase(PaytableBoxElement.CaseMystery);
                        BaseView.paytableBoxElement.chooseMultiplier(mysterySymbol - 1, true);
                        BaseView.paytableBoxElement.chooseSymbol(mysterySymbol - 1, true);
                    }
                }
            }
        }

        if (CustomUtils.isRespin()) {
            this.markSymbolInXbox(GameManager.baseResponse);
        } else if (CustomUtils.isRespinLast()) {
            this.markSymbolInXbox(GameManager.baseResponse);
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    const baseInitDataReels = GameManager.getBaseInitDataReels();
                    if (baseInitDataReels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        if (!Utils.isNullOrUndefined(this._teasingMystery)) {
                            this._machine.addElementInstance({ teasing: [this._teasingMystery] });
                        }
                        this._machine.initialize(this._symbols, baseInitDataReels);

                        // Custom for BigX
                        const baseInitDataCustomRespinReels = GameManager.getBaseInitDataCustomRespinReels();
                        if (baseInitDataCustomRespinReels != null) {
                            this._machine.addRespinReels(baseInitDataCustomRespinReels);
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in initData");
                        }
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            if (!Utils.isNullOrUndefined(this._teasingMystery)) {
                                this._winAnimator.addElementInstance({ teasing: [this._teasingMystery] });
                            }
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                this._symbolsContainer,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                            this._winAnimator.reelsContaier = BaseView.Reels;
                            this._winAnimator.paytableBoxElement = BaseView.paytableBoxElement; //todo Unused? Remove?
                            this._winAnimator.stickyContainer = this._stickyContainer;
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.toggleElements(false);
    }

    setIsDesktopChannel(isDesktopChannel: boolean = false): void {
        BaseView.isDesktopChannel = isDesktopChannel;
    }

    disableLogo(): void {
        if (BaseView.Logo != null) {
            this.removeChild(BaseView.Logo);
            BaseView.Logo = null;
        }
    }

    toggleElements(visible: boolean, long: boolean = false): void {
        if (visible) {
            if (long) {
                if (!Utils.isNullOrUndefined(BaseView.Reels)) {
                    Tweens.removeTweens(BaseView.Reels, "all");
                    Tweens.alphaTween(BaseView.Reels, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (!Utils.isNullOrUndefined(BaseView.paytableBoxElement.paytableBox)) {
                    Tweens.removeTweens(BaseView.paytableBoxElement.paytableBox, "all");
                    Tweens.alphaTween(BaseView.paytableBoxElement.paytableBox, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                    BaseView.paytableBoxElement.paytableBox.visible = true;
                }
                if (!Utils.isNullOrUndefined(this._paylines)) {
                    Tweens.removeTweens(this._paylines, "all");
                    Tweens.alphaTween(this._paylines, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                        tweenDelay: 15,
                    });
                    this._paylines.visible = true;
                }
                if (!Utils.isNullOrUndefined(this._winAnimator)) {
                    Tweens.removeTweens(this._winAnimator, "all");
                    Tweens.alphaTween(this._winAnimator, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                    this._winAnimator.visible = true;
                }
                if (!Utils.isNullOrUndefined(this._symbolsContainer)) {
                    Tweens.removeTweens(this._symbolsContainer, "all");
                    Tweens.alphaTween(this._symbolsContainer, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                    this._symbolsContainer.visible = true;
                }
                if (!Utils.isNullOrUndefined(this._noWinContainer)) {
                    Tweens.removeTweens(this._noWinContainer, "all");
                    Tweens.alphaTween(this._noWinContainer, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                    this._noWinContainer.visible = true;
                }
                if (!Utils.isNullOrUndefined(BaseView.ReflectionX)) {
                    Tweens.removeTweens(BaseView.ReflectionX, "all");
                    Tweens.alphaTween(BaseView.ReflectionX, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                        tweenDelay: 15,
                    });
                    BaseView.ReflectionX.visible = true;
                }
                if (!Utils.isNullOrUndefined(BaseView.XBox)) {
                    Tweens.removeTweens(BaseView.XBox, "all");
                    Tweens.alphaTween(BaseView.XBox, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                        tweenDelay: 15,
                    });
                    BaseView.XBox.visible = true;
                }
            } else {
                if (!Utils.isNullOrUndefined(this._noWinContainer)) {
                    this._noWinContainer.alpha = 1;
                    this._noWinContainer.visible = visible;
                }
                if (!Utils.isNullOrUndefined(this._symbolsContainer)) {
                    this._symbolsContainer.alpha = 1;
                    this._symbolsContainer.visible = visible;
                }
                if (!Utils.isNullOrUndefined(this._paylines)) {
                    this._paylines.alpha = 1;
                    this._paylines.visible = visible;
                }
                if (!Utils.isNullOrUndefined(this._winAnimator)) {
                    this._winAnimator.alpha = 1;
                    this._winAnimator.visible = visible;
                }
                if (!Utils.isNullOrUndefined(BaseView.paytableBoxElement)) {
                    BaseView.paytableBoxElement.paytableBox.visible = visible;
                }
                if (!Utils.isNullOrUndefined(BaseView.ReflectionX)) {
                    BaseView.ReflectionX.alpha = 1;
                    BaseView.ReflectionX.visible = visible;
                }
                if (!Utils.isNullOrUndefined(BaseView.XBox)) {
                    BaseView.XBox.alpha = 1;
                    BaseView.XBox.visible = visible;
                }
            }
        } else {
            if (long) {
                if (!Utils.isNullOrUndefined(BaseView.Reels)) {
                    Tweens.removeTweens(BaseView.Reels, "all");
                    Tweens.alphaTween(BaseView.Reels, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (!Utils.isNullOrUndefined(BaseView.paytableBoxElement.paytableBox)) {
                    Tweens.removeTweens(BaseView.paytableBoxElement.paytableBox, "all");
                    Tweens.alphaTween(BaseView.paytableBoxElement.paytableBox, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (!Utils.isNullOrUndefined(this._paylines)) {
                    Tweens.removeTweens(this._paylines, "all");
                    Tweens.alphaTween(this._paylines, {
                        alphas: [1, 0],
                        tweenDuration: 5,
                    });
                }
                if (!Utils.isNullOrUndefined(this._winAnimator)) {
                    Tweens.removeTweens(this._winAnimator, "all");
                    Tweens.alphaTween(this._winAnimator, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (!Utils.isNullOrUndefined(this._symbolsContainer)) {
                    Tweens.removeTweens(this._symbolsContainer, "all");
                    Tweens.alphaTween(this._symbolsContainer, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (!Utils.isNullOrUndefined(this._noWinContainer)) {
                    Tweens.removeTweens(this._noWinContainer, "all");
                    Tweens.alphaTween(this._noWinContainer, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (!Utils.isNullOrUndefined(BaseView.ReflectionX)) {
                    Tweens.removeTweens(BaseView.ReflectionX, "all");
                    Tweens.alphaTween(BaseView.ReflectionX, {
                        alphas: [1, 0],
                        tweenDuration: 5,
                    });
                }
                if (!Utils.isNullOrUndefined(BaseView.XBox)) {
                    Tweens.removeTweens(BaseView.XBox, "all");
                    Tweens.alphaTween(BaseView.XBox, {
                        alphas: [1, 0],
                        tweenDuration: 5,
                    });
                }
            } else {
                if (!Utils.isNullOrUndefined(this._noWinContainer)) {
                    this._noWinContainer.alpha = 1;
                    this._noWinContainer.visible = visible;
                }
                if (!Utils.isNullOrUndefined(this._symbolsContainer)) {
                    this._symbolsContainer.alpha = 1;
                    this._symbolsContainer.visible = visible;
                }
                if (!Utils.isNullOrUndefined(this._paylines)) {
                    this._paylines.alpha = 1;
                    this._paylines.visible = visible;
                }
                if (!Utils.isNullOrUndefined(this._winAnimator)) {
                    this._winAnimator.alpha = 1;
                    this._winAnimator.visible = visible;
                }
                if (!Utils.isNullOrUndefined(BaseView.paytableBoxElement)) {
                    BaseView.paytableBoxElement.paytableBox.visible = visible;
                }
                if (!Utils.isNullOrUndefined(BaseView.ReflectionX)) {
                    BaseView.ReflectionX.alpha = 1;
                    BaseView.ReflectionX.visible = visible;
                }
                if (!Utils.isNullOrUndefined(BaseView.XBox)) {
                    BaseView.XBox.alpha = 1;
                    BaseView.XBox.visible = visible;
                }
            }
        }
    }

    //******************************************************************************************************************
    //  TEASING MYSTERY
    //*****************************************************************************************************************

    teasingMysteryTeasing(): void {
        if (!this._inTeasing) {
            this._inTeasing = true;

            PlayList.play("SND_MysteryTeaser");
        }

        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            BaseView.Background.goToCase("BASE_TO_MYSTERY");
        }

        if (!Utils.isNullOrUndefined(BaseView.paytableBoxElement)) {
            BaseView.paytableBoxElement.reset({ symbolsGlowOff: true, multipliersGlowOff: true });
        }
    }

    teasingMysteryTeasingWin(): void {
        PlayList.stop("SND_MysteryTeaser", 30);
        PlayList.play("SND_MysteryTeaserWin");

        if (!Utils.isNullOrUndefined(BaseView.XBox)) {
            BaseView.XBox.goToCase("DEFAULT");
        }
    }

    teasingMysteryTeasingFail(): void {
        PlayList.stop("SND_MysteryTeaser", 30);
        PlayList.play("SND_MysteryTeaserFail");

        if (!Utils.isNullOrUndefined(BaseView.XBox)) {
            BaseView.XBox.goToCase("DEFAULT");
        }

        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            BaseView.Background.goToCase("MYSTERY_TO_BASE");
        }

        if (!Utils.isNullOrUndefined(BaseView.Reels)) {
            BaseView.Reels.goToCase("RESET");
        }

        BaseView.paytableBoxElement.reset({ symbolsGlowOff: true, multipliersGlowOff: true });
    }

    public teasingMysteryAnimateOnReel(reelIndex: number): void {
        if (this._teasingMystery.teasingEnabled) {
            this._controller.enableSpinStopButton();

            this._winAnimator.highlightReel(reelIndex, "WIN", ReelsHighlightOptions.loop);
        }
    }

    private teasingMysteryReelStopping(reelIndex: number): void {
        if (DEBUG) {
            this.log("reel is stopping:" + reelIndex);
        }
    }

    initIntro(): void {
        StageController.baseStage = true;
        this._wasFinished = true;

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.loop("SND_BaseRecapLoop");

            if (!Utils.isNullOrUndefined(BaseView.Reels)) {
                BaseView.Reels.goToCase("WIN");
            }
        } else {
            PlayList.loop("SND_BaseLoop");

            if (!Utils.isNullOrUndefined(BaseView.Reels)) {
                BaseView.Reels.goToCase("NORMAL");
            }
        }

        this.toggleElements(true);

        if (this._winAnimator.hasMysteryWin(GameManager.baseResponse.wins)) {
            if (!Utils.isNullOrUndefined(BaseView.Background)) {
                BaseView.Background.goToCase("MYSTERY");
            }
        } else if (CustomUtils.isRespin() || CustomUtils.isRespinLast()) {
            if (!Utils.isNullOrUndefined(BaseView.Background)) {
                BaseView.Background.goToCase("BONUS");
            }
        } else {
            if (!Utils.isNullOrUndefined(BaseView.Background)) {
                BaseView.Background.goToCase("BASE");
            }
        }

        if (CustomUtils.isRespinLast()) {
            if (!Utils.isNullOrUndefined(BaseView.Logo)) {
                BaseView.Logo.goToCase("TEASE");
            }
        } else {
            if (!Utils.isNullOrUndefined(BaseView.Logo)) {
                BaseView.Logo.goToCase("BASE");
            }
        }

        if (!Utils.isNullOrUndefined(BaseView.XBox)) {
            BaseView.XBox.goToCase("DEFAULT");
        }

        if (GameManager.baseResponse != null && GameManager.baseResponse.hasOwnProperty("customData")) {
            if (GameManager.baseResponse.customData.hasOwnProperty("bigXFeaturePayout")) {
                let drawnSymbol = GameManager.baseResponse.customData.bigXFeaturePayout;

                BaseView.paytableBoxElement.reset({ symbolsGlowOff: true, multipliersGlowOff: true });
                BaseView.paytableBoxElement.chooseMultiplier(drawnSymbol - 1, false);
                BaseView.paytableBoxElement.chooseSymbol(drawnSymbol - 1, false);
            }
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
        if (CustomUtils.isRespinLast()) {
            this._winAnimator.showWinAnimationFrame(150);
        }

        if (CustomUtils.isRespin()) {
            this.markSymbolInXbox(GameManager.baseResponse);
            this.stickScattersSymbols(true);
        } else if (CustomUtils.isRespinLast()) {
            this.markSymbolInXbox(GameManager.baseResponse);
            this.stickScattersSymbols(true);
            this.switchScattersSymbolsToCurrentSymbols(Symbol.CaseWin);
        }

        if (CustomUtils.isRespinLast()) {
            if (!Utils.isNullOrUndefined(this._paylines)) {
                this._paylines.goToCase("RESPIN", 1);
                this._paylines.goToCase("RESPIN", 2);
                this._paylines.goToCase("RESPIN", 3);
                this._paylines.goToCase("RESPIN", 4);
                this._paylines.goToCase("RESPIN", 5);
            }
        }
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_RespinLoop", 15);
        PlayList.stop("SND_RespinWin", 15);
        PlayList.loop("SND_BaseLoop");

        if (!Utils.isNullOrUndefined(this._paylinesSelector)) {
            this._paylinesSelector.reset();
        }

        this._winAnimator.stopAnimation();
        this._winAnimator.reset(true);

        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            if (["BASE_TO_MYSTERY", "MYSTERY"].indexOf(BaseView.Background.getCurrentCase()) != -1) {
                BaseView.Background.goToCase("MYSTERY_TO_BASE");
            }
            if (["BASE_TO_BONUS", "BONUS"].indexOf(BaseView.Background.getCurrentCase()) != -1) {
                BaseView.Background.goToCase("BONUS_TO_BASE");
            }
        }

        if (!Utils.isNullOrUndefined(BaseView.Reels)) {
            BaseView.Reels.goToCase("NORMAL");
        }

        if (!Utils.isNullOrUndefined(BaseView.Logo)) {
            BaseView.Logo.goToCase("BASE");
        }

        this.resetXbox();

        BaseView.paytableBoxElement.reset({
            symbolsOn: true,
            symbolsNoWinOn: false,
            symbolsGlowOff: true,
            multipliersGlowOff: false,
        });
    }

    spin(spinBitmask: number): void {
        this._wasFinished = false;
        StageController.baseStage = true;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_RespinLoop", 15);
        PlayList.stop("SND_RespinWin", 15);
        PlayList.loop("SND_BaseLoop");

        if (!Utils.isNullOrUndefined(this._paylinesSelector)) {
            this._paylinesSelector.reset();
        }

        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            if (["BASE_TO_MYSTERY", "MYSTERY"].indexOf(BaseView.Background.getCurrentCase()) != -1) {
                BaseView.Background.goToCase("MYSTERY_TO_BASE");
            }
            if (["BASE_TO_BONUS", "BONUS"].indexOf(BaseView.Background.getCurrentCase()) != -1) {
                BaseView.Background.goToCase("BONUS_TO_BASE");
            }
        }

        if (!Utils.isNullOrUndefined(BaseView.Reels)) {
            BaseView.Reels.goToCase("NORMAL");
        }

        if (!Utils.isNullOrUndefined(BaseView.Logo)) {
            BaseView.Logo.goToCase("BASE");
        }

        this.resetXbox();

        BaseView.paytableBoxElement.reset({
            symbolsOn: true,
            symbolsNoWinOn: false,
            symbolsGlowOff: true,
            multipliersGlowOff: false,
        });

        //this.removeScattersSymbols();
        this.switchScattersSymbolsToCurrentSymbols();

        this._machine.spinStart(
            this._winAnimator.reset(true, true),
            true,
            this._winAnimator.currentSymbols,
            spinBitmask,
        );
    }

    respin(spinBitmask: number): void {
        StageController.baseStage = true;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_BaseLoop");
        PlayList.loop("SND_RespinLoop", 0);

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        if (!Utils.isNullOrUndefined(BaseView.Logo)) {
            BaseView.Logo.goToCase("TEASE");
        }

        BaseView.paytableBoxElement.reset({
            symbolsOn: false,
            symbolsNoWinOn: true,
            symbolsGlowOff: true,
            multipliersGlowOff: true,
        });

        this._machine.respinStart(
            this._winAnimator.reset(true, true),
            false,
            this._winAnimator.currentSymbols,
            spinBitmask,
        );

        if (!Utils.isNullOrUndefined(this._paylines)) {
            // this need to be placed after _machine.respinStart, because it can reset paylines.
            this._paylines.goToCase("RESPIN", 1);
            this._paylines.goToCase("RESPIN", 2);
            this._paylines.goToCase("RESPIN", 3);
            this._paylines.goToCase("RESPIN", 4);
            this._paylines.goToCase("RESPIN", 5);
            //this._paylines.goToCase('RESPIN', 5);
        }
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    respinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();
        this._inTeasing = false;

        if (GameManager.baseResponse.wins.length > 0) {
            if (!Utils.isNullOrUndefined(BaseView.Reels)) {
                BaseView.Reels.goToCase("WIN");
            }
        }

        if (CustomUtils.isRespinEnabled()) {
            if (!Utils.isNullOrUndefined(BaseView.Background)) {
                BaseView.Background.goToCase("BASE_TO_BONUS");
            }
        }

        if (CustomUtils.isRespin()) {
            this.markSymbolInXbox(GameManager.baseResponse);
            this.stickScattersSymbols();
        } else if (CustomUtils.isRespinLast()) {
            this.markSymbolInXbox(GameManager.baseResponse);
            this.switchScattersSymbolsToCurrentSymbols(Symbol.CaseNormal);
        }

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    /**
     * Vola sa, ak v animacii bol emitnuty event "finished".
     */
    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    /**
     * Vola sa po skonceni vyhernej animacie, ale este tesne pred cycle win.
     */
    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            // PlayList.setVolume('SND_BaseLoop', 0.25, 15);
            PlayList.stop("SND_BaseLoop", 15);

            if (CustomUtils.isRespinLast()) {
                PlayList.stop("SND_RespinLoop", 15);
                PlayList.play("SND_RespinWin", 15);
                PlayList.loop("SND_BaseRecapLoop", 360);
            } else {
                PlayList.loop("SND_BaseRecapLoop", 15);
            }
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    /**
     * @param long
     *      True - from gamble
     *      False - from freespins
     */
    intro(long: boolean = false): void {
        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            BaseView.Background.goToCase("BASE");
        }

        PlayList.loop("SND_BaseLoop").resetVolume();

        if (long) {
            this._winAnimator.resetIdleTimer(true, false);

            this.toggleElements(true, true);

            if (!Utils.isNullOrUndefined(BaseView.Reels)) {
                if (BaseView.isDesktopChannel) {
                    BaseView.Reels.goToCase("NORMAL");
                } else {
                    BaseView.Reels.goToCase("IN");
                }
            }
        } else {
            this.toggleElements(true, false);

            if (!Utils.isNullOrUndefined(this._winAnimator)) {
                Tweens.removeTweens(this._winAnimator, "all");
                Tweens.alphaTween(this._winAnimator, {
                    alphas: [0, 1],
                    tweenDuration: 30,
                });
            }
        }

        if (!Utils.isNullOrUndefined(BaseView.Logo)) {
            BaseView.Logo.goToCase("BASE");
        }
        if (!Utils.isNullOrUndefined(BaseView.XBox)) {
            BaseView.XBox.goToCase("DEFAULT");
        }

        if (!Utils.isNullOrUndefined(BaseView.paytableBoxElement)) {
            BaseView.paytableBoxElement.reset({ symbolsGlowOff: true, multipliersGlowOff: false });
            BaseView.paytableBoxElement.paytableBox.goToCase(PaytableBoxElement.CaseShowLong);
        }

        this._introDelay.start(30);
    }

    introFinished(): void {
        this._introDelay.stop();

        StageController.baseStage = true;

        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
        }
        this._controller.introFinished();
    }

    outro(isDesktopChannel: boolean = false): void {
        StageController.baseStage = true;

        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.stop("SND_FSWinLoop");
        PlayList.stop("SND_RespinLoop");
        PlayList.stop("SND_RespinWin");

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        if (!Utils.isNullOrUndefined(this._paylinesSelector)) {
            this._paylinesSelector.reset();
        }

        this.toggleElements(false, true);

        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            if (["BASE_TO_MYSTERY", "MYSTERY"].indexOf(BaseView.Background.getCurrentCase()) != -1) {
                BaseView.Background.goToCase("MYSTERY_TO_BASE");
            }
            if (["BASE_TO_BONUS", "BONUS"].indexOf(BaseView.Background.getCurrentCase()) != -1) {
                BaseView.Background.goToCase("BONUS_TO_BASE");
            }
        }

        BaseView.paytableBoxElement.reset({ symbolsGlowOff: true, multipliersGlowOff: true });

        PlayList.play("SND_GambleTransition");
        PlayList.stop("SND_BaseLoop");

        if (wasFinished) {
            this.outroFinished();
        } else {
            if (inHighWins) {
                this._outroDelay.start(60);
            } else {
                this._outroDelay.start(30);
            }
        }
    }

    outroFinished(): void {
        this._outroDelay.stop();

        this._controller.outroFinished();
        this._winAnimator.resetTimer();

        if (!Utils.isNullOrUndefined(BaseView.Logo)) {
            BaseView.Logo.goToCase("EMPTY");
        }

        if (!Utils.isNullOrUndefined(BaseView.XBox)) {
            BaseView.XBox.goToCase("EMPTY");
        }
    }

    selectPayline(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.select();
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }

    public stickScattersSymbols(init = false): number {
        return this._winAnimator.stickScatterSymbols(GameManager.baseResponse, init);
    }

    public removeScattersSymbols(): void {
        this._winAnimator.removeScattersSymbols();
    }

    public switchScattersSymbolsToCurrentSymbols(caseType?: string): void {
        this._winAnimator.switchScattersSymbolsToCurrentSymbols(caseType);
    }

    /**
     * Active/Disabled
     */
    protected markSymbolInXbox(baseResponseData: Communication.Game.GameModuleBaseResponse): void {
        let markedPositions = 0;
        if (!Utils.isNullOrUndefined(baseResponseData)) {
            if (baseResponseData.hasOwnProperty("screen") && !Utils.isNullOrUndefined(baseResponseData.screen)) {
                if (baseResponseData.screen.hasOwnProperty("reels")) {
                    let reelsData: Array<Communication.Game.IReel> = baseResponseData.screen.reels;
                    let children: Object = BaseView.XBox.getAllChildElements();

                    for (let reelIndex = 0; reelIndex < reelsData.length; reelIndex++) {
                        if (reelsData[reelIndex].hasOwnProperty("transform")) {
                            for (
                                let symbolIndex = 0;
                                symbolIndex < reelsData[reelIndex].transform.length;
                                symbolIndex++
                            ) {
                                if (reelsData[reelIndex].transform[symbolIndex] === Settings.SymbolBigXSpins) {
                                    let select = reelIndex + "_" + symbolIndex;
                                    if (children.hasOwnProperty("Active_" + select)) {
                                        let image = children["Active_" + select] as ImageElement;
                                        image.visible = true;

                                        Tweens.removeTweens(image, "all");
                                        Tweens.customTween(image, {
                                            tween: "alpha",
                                            tweenValues: [0, 1, 1, 0, 1],
                                            tweenDuration: [2, 13, 2, 13],
                                        });
                                    }
                                }
                            }
                        }

                        if (reelsData[reelIndex].hasOwnProperty("overlay")) {
                            for (
                                let symbolIndex = 0;
                                symbolIndex < reelsData[reelIndex].overlay.length;
                                symbolIndex++
                            ) {
                                if (reelsData[reelIndex].overlay[symbolIndex] === Settings.SymbolBigXSpins) {
                                    markedPositions++;

                                    let select = reelIndex + "_" + symbolIndex;
                                    if (children.hasOwnProperty("Active_" + select)) {
                                        let image = children["Active_" + select] as ImageElement;
                                        image.visible = true;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (markedPositions == 9) {
            PlayList.play("SND_BigXWin");

            let children: Object = BaseView.XBox.getAllChildElements();
            let number = children["Number"] as ImageElement;

            let frameGlow: ImageElement = null;
            if (<ImageElement>children["FrameGlow"].visible) {
                frameGlow = <ImageElement>children["FrameGlow"];
            } else if (<ImageElement>children["FrameGlowPortrait"].visible) {
                frameGlow = <ImageElement>children["FrameGlowPortrait"];
            }

            if (!Utils.isNullOrUndefined(number)) {
                Tweens.removeTweens(number, "all");
                Tweens.customTween(number, {
                    tween: "alpha",
                    tweenValues: [0.1, 0.9, 0.9, 0, 0.1],
                    tweenDuration: [2, 13, 2, 13],
                    tweenLoop: true,
                });
            }

            if (!Utils.isNullOrUndefined(frameGlow)) {
                Tweens.removeTweens(frameGlow, "all");
                Tweens.customTween(frameGlow, {
                    tween: "alpha",
                    tweenValues: [0, 1, 1, 0, 0],
                    tweenDuration: [2, 13, 2, 13],
                    tweenLoop: true,
                });
            }

            Object.keys(children)
                .filter((key: string) => {
                    return key.indexOf("Active") > -1;
                })
                .forEach((childName) => {
                    let image = children[childName] as ImageElement;
                    Tweens.removeTweens(image, "all");
                    Tweens.customTween(image, {
                        tween: "alpha",
                        tweenValues: [0, 1, 1, 0, 0],
                        tweenDuration: [2, 13, 2, 13],
                        tweenLoop: true,
                    });
                });
        }
    }

    /**
     * Reset all Active and Disabled elements.
     */
    protected resetXbox(): void {
        if (!Utils.isNullOrUndefined(BaseView.XBox)) {
            let children: Object = BaseView.XBox.getAllChildElements();
            let number = children["Number"] as ImageElement;

            let frameGlow: ImageElement = null;
            if (<ImageElement>children["FrameGlow"].visible) {
                frameGlow = <ImageElement>children["FrameGlow"];
            } else if (<ImageElement>children["FrameGlowPortrait"].visible) {
                frameGlow = <ImageElement>children["FrameGlowPortrait"];
            }

            if (!Utils.isNullOrUndefined(number)) {
                Tweens.removeTweens(number, "all");
                number.alpha = 0.1;
            }

            if (!Utils.isNullOrUndefined(frameGlow)) {
                Tweens.removeTweens(frameGlow, "all");
                frameGlow.alpha = 0;
            }

            Object.keys(children)
                .filter((key: string) => {
                    return key.indexOf("Active") > -1; //|| key.indexOf('Disabled') > -1
                })
                .forEach((childName) => {
                    (children[childName] as ImageElement).visible = false;
                });
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/